import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import './TeacherSections.css';

const TeacherSectionCourses = ({ setActiveSection }) => {
  const { t, i18n } = useTranslation('teacher');
  
  // Format date based on current language
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const locale = i18n.language === 'ar' ? 'ar-SA' : i18n.language === 'fr' ? 'fr-FR' : 'en-US';
    return date.toLocaleDateString(locale, {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit'
    });
  };
  const [courses, setCourses] = useState([
    {
      id: 1,
      title: 'Advanced English Grammar',
      category: 'Grammar',
      price: 49.99,
      status: 'active',
      enrollments: 234,
      rating: 4.8,
      lastUpdated: '2024-03-10'
    },
    {
      id: 2,
      title: 'Business English Speaking',
      category: 'Speaking',
      price: 39.99,
      status: 'active',
      enrollments: 156,
      rating: 4.9,
      lastUpdated: '2024-03-08'
    },
    {
      id: 3,
      title: 'IELTS Preparation Course',
      category: 'Exam Prep',
      price: 79.99,
      status: 'draft',
      enrollments: 0,
      rating: 0,
      lastUpdated: '2024-03-05'
    }
  ]);

  const [showAddCourse, setShowAddCourse] = useState(false);
  const [editingCourse, setEditingCourse] = useState(null);
  const [courseForm, setCourseForm] = useState({
    title: '',
    category: '',
    price: '',
    level: '',
    description: '',
    tags: ''
  });

  const categories = [
    'Grammar', 'Speaking', 'Listening', 'Reading', 'Writing', 
    'Vocabulary', 'Exam Prep', 'Business English', 'Conversation'
  ];

  const levels = ['Beginner', 'Intermediate', 'Advanced'];

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setCourseForm(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    if (editingCourse) {
      // Update existing course
      setCourses(prev => prev.map(course => 
        course.id === editingCourse.id 
          ? { ...course, ...courseForm, lastUpdated: new Date().toISOString().split('T')[0] }
          : course
      ));
      setEditingCourse(null);
    } else {
      // Add new course
      const newCourse = {
        id: Date.now(),
        ...courseForm,
        status: 'draft',
        enrollments: 0,
        rating: 0,
        lastUpdated: new Date().toISOString().split('T')[0]
      };
      setCourses(prev => [...prev, newCourse]);
    }
    
    setCourseForm({
      title: '',
      category: '',
      price: '',
      level: '',
      description: '',
      tags: ''
    });
    setShowAddCourse(false);
  };

  const editCourse = (course) => {
    setEditingCourse(course);
    setCourseForm({
      title: course.title,
      category: course.category,
      price: course.price.toString(),
      level: course.level || '',
      description: course.description || '',
      tags: course.tags || ''
    });
    setShowAddCourse(true);
  };

  const deleteCourse = (courseId) => {
    if (window.confirm(t('courses.actions.deleteConfirm'))) {
      setCourses(prev => prev.filter(course => course.id !== courseId));
    }
  };

  const toggleCourseStatus = (courseId) => {
    setCourses(prev => prev.map(course => 
      course.id === courseId 
        ? { ...course, status: course.status === 'active' ? 'draft' : 'active' }
        : course
    ));
  };

  const generateStars = (rating) => {
    const stars = [];
    for (let i = 1; i <= 5; i++) {
      stars.push(
        <span key={i} className={`star ${i <= rating ? 'filled' : ''}`}>
          ★
        </span>
      );
    }
    return stars;
  };

  return (
    <div className="teacher-main-content">
      <div className="teacher-content-header">
        <h1 className="teacher-content-title">{t('courses.title')}</h1>
        <p className="teacher-content-subtitle">{t('courses.subtitle')}</p>
      </div>

      {/* Course Management Section */}
      <div className="teacher-section">
        <div className="teacher-section-header">
          <div className="teacher-section-title">
            <i className="material-icons">menu_book</i>
            {t('courses.courseManagement')}
          </div>
          <button 
            className="teacher-btn primary" 
            onClick={() => {
              setEditingCourse(null);
              setCourseForm({
                title: '',
                category: '',
                price: '',
                level: '',
                description: '',
                tags: ''
              });
              setShowAddCourse(true);
            }}
          >
            <i className="material-icons">add</i>
            {t('courses.addNew')}
          </button>
        </div>

        {/* Course Statistics */}
        <div className="teacher-overview-grid">
          <div className="teacher-overview-card">
            <div className="teacher-overview-header">
              <span className="teacher-overview-title">{t('courses.totalCourses')}</span>
              <div className="teacher-overview-icon">
                <i className="material-icons">menu_book</i>
              </div>
            </div>
            <div className="teacher-overview-value">{courses.length}</div>
            <div className="teacher-overview-change">
              <i className="material-icons">trending_up</i>
              {t('courses.activeCoursesLabel')}
            </div>
          </div>

          <div className="teacher-overview-card success">
            <div className="teacher-overview-header">
              <span className="teacher-overview-title">{t('courses.activeCourses')}</span>
              <div className="teacher-overview-icon">
                <i className="material-icons">check_circle</i>
              </div>
            </div>
            <div className="teacher-overview-value">
              {courses.filter(c => c.status === 'active').length}
            </div>
            <div className="teacher-overview-change">
              <i className="material-icons">trending_up</i>
              {t('courses.published')}
            </div>
          </div>

          <div className="teacher-overview-card accent">
            <div className="teacher-overview-header">
              <span className="teacher-overview-title">{t('courses.totalEnrollments')}</span>
              <div className="teacher-overview-icon">
                <i className="material-icons">people</i>
              </div>
            </div>
            <div className="teacher-overview-value">
              {courses.reduce((sum, course) => sum + course.enrollments, 0)}
            </div>
            <div className="teacher-overview-change">
              <i className="material-icons">trending_up</i>
              {t('courses.studentsEnrolled')}
            </div>
          </div>

          <div className="teacher-overview-card info">
            <div className="teacher-overview-header">
              <span className="teacher-overview-title">{t('courses.averageRating')}</span>
              <div className="teacher-overview-icon">
                <i className="material-icons">star</i>
              </div>
            </div>
            <div className="teacher-overview-value">
              {(courses.reduce((sum, course) => sum + course.rating, 0) / courses.filter(c => c.rating > 0).length || 0).toFixed(1)}
            </div>
            <div className="teacher-overview-change">
              <i className="material-icons">star</i>
              {t('courses.outOf5Stars')}
            </div>
          </div>
        </div>

        {/* Courses Table */}
        <div className="teacher-table-container">
          <table className="teacher-data-table">
            <thead>
              <tr>
                <th>{t('courses.table.title')}</th>
                <th>{t('courses.table.category')}</th>
                <th>{t('courses.table.price')}</th>
                <th>{t('courses.table.status')}</th>
                <th>{t('courses.table.enrollments')}</th>
                <th>{t('courses.table.rating')}</th>
                <th>{t('courses.table.lastUpdated')}</th>
                <th>{t('courses.table.actions')}</th>
              </tr>
            </thead>
            <tbody>
              {courses.map((course) => (
                <tr key={course.id}>
                  <td>
                    <div className="course-title">
                      <strong>{course.title}</strong>
                    </div>
                  </td>
                  <td>
                    <span className="category-badge">{course.category}</span>
                  </td>
                  <td>${course.price}</td>
                  <td>
                    <span className={`status-badge ${course.status}`}>
                      {course.status === 'active' ? t('courses.status.active') : t('courses.status.draft')}
                    </span>
                  </td>
                  <td>{course.enrollments}</td>
                  <td>
                    <div className="course-rating">
                      {course.rating > 0 ? (
                        <>
                          {generateStars(course.rating)}
                          <span className="rating-text">{course.rating}</span>
                        </>
                      ) : (
                        <span className="no-rating">{t('courses.noRatings')}</span>
                      )}
                    </div>
                  </td>
                  <td>{formatDate(course.lastUpdated)}</td>
                  <td>
                    <div className="course-actions">
                      <button 
                        className="teacher-btn secondary" 
                        onClick={() => editCourse(course)}
                        title={t('courses.actions.edit')}
                      >
                        <i className="material-icons">edit</i>
                      </button>
                      <button 
                        className="teacher-btn secondary" 
                        onClick={() => toggleCourseStatus(course.id)}
                        title={course.status === 'active' ? t('courses.actions.unpublish') : t('courses.actions.publish')}
                      >
                        <i className="material-icons">
                          {course.status === 'active' ? 'visibility_off' : 'visibility'}
                        </i>
                      </button>
                      <button 
                        className="teacher-btn danger" 
                        onClick={() => deleteCourse(course.id)}
                        title={t('courses.actions.delete')}
                      >
                        <i className="material-icons">delete</i>
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Add/Edit Course Modal */}
      {showAddCourse && (
        <div className="teacher-modal-overlay" onClick={() => setShowAddCourse(false)}>
          <div className="teacher-modal-content" onClick={(e) => e.stopPropagation()}>
            <button 
              className="teacher-modal-close" 
              onClick={() => setShowAddCourse(false)}
            >
              ×
            </button>
            
            <h2>{editingCourse ? t('courses.editCourse') : t('courses.addNew')}</h2>
            
            <form className="teacher-form" onSubmit={handleSubmit}>
              <div className="teacher-form-group">
                <label htmlFor="courseTitle">{t('courses.form.courseTitle')} *</label>
                <input
                  type="text"
                  id="courseTitle"
                  name="title"
                  value={courseForm.title}
                  onChange={handleInputChange}
                  required
                  placeholder={t('courses.form.enterTitle')}
                />
              </div>

              <div className="teacher-form-group">
                <label htmlFor="courseCategory">{t('courses.form.category')} *</label>
                <select
                  id="courseCategory"
                  name="category"
                  value={courseForm.category}
                  onChange={handleInputChange}
                  required
                >
                  <option value="">{t('courses.form.selectCategory')}</option>
                  {categories.map(category => (
                    <option key={category} value={category}>{category}</option>
                  ))}
                </select>
              </div>

              <div className="teacher-form-group">
                <label htmlFor="coursePrice">{t('courses.form.price')} ($) *</label>
                <input
                  type="number"
                  id="coursePrice"
                  name="price"
                  value={courseForm.price}
                  onChange={handleInputChange}
                  min="0"
                  step="0.01"
                  required
                  placeholder="0.00"
                />
              </div>

              <div className="teacher-form-group">
                <label htmlFor="courseLevel">{t('courses.form.level')}</label>
                <select
                  id="courseLevel"
                  name="level"
                  value={courseForm.level}
                  onChange={handleInputChange}
                >
                  <option value="">{t('courses.form.selectLevel')}</option>
                  {levels.map(level => (
                    <option key={level} value={level}>{level}</option>
                  ))}
                </select>
              </div>

              <div className="teacher-form-group">
                <label htmlFor="courseDescription">{t('courses.form.description')}</label>
                <textarea
                  id="courseDescription"
                  name="description"
                  value={courseForm.description}
                  onChange={handleInputChange}
                  rows="4"
                  placeholder={t('courses.form.enterDescription')}
                />
              </div>

              <div className="teacher-form-group">
                <label htmlFor="courseTags">{t('courses.form.tags')}</label>
                <input
                  type="text"
                  id="courseTags"
                  name="tags"
                  value={courseForm.tags}
                  onChange={handleInputChange}
                  placeholder={t('courses.form.enterTags')}
                />
              </div>

              <div className="teacher-form-actions">
                <button type="submit" className="teacher-btn primary">
                  <i className="material-icons">save</i>
                  {editingCourse ? t('courses.form.update') : t('courses.form.save')}
                </button>
                <button 
                  type="button" 
                  className="teacher-btn secondary"
                  onClick={() => setShowAddCourse(false)}
                >
                  {t('courses.form.cancel')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default TeacherSectionCourses; 